import { useState, type ReactNode } from 'react';
import {
  Info,
  X,
  RefreshCw,
  ShieldCheck,
  Clock,
  AlertTriangle,
  Wrench,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import type { TenantStatus } from '../types.ts';

interface Props {
  tenantNome: string;
  tenantSlug: string;
  status: TenantStatus;
  uptimeMes: number | null;
  uptime90dias: number | null;
  uptime12meses: number | null;
  incidentes30d: number;
  latenciaMs: number | null;
}

const STATUS_LABEL: Record<TenantStatus, string> = {
  UP: 'Operacional',
  DOWN: 'Indisponível',
  DEGRADADO: 'Degradado',
};

const STATUS_COLOR: Record<TenantStatus, string> = {
  UP: 'var(--color-status-up)',
  DOWN: 'var(--color-status-down)',
  DEGRADADO: 'var(--color-status-degraded)',
};

function fmtPct(val: number | null): string {
  if (val === null) return '—';
  return `${val.toFixed(2)}%`;
}

// Itens explicativos do painel "Como calculamos a disponibilidade".
const CALCULO_ITENS: { Icon: LucideIcon; texto: string }[] = [
  {
    Icon: RefreshCw,
    texto: 'As verificações são realizadas automaticamente em intervalos regulares.',
  },
  {
    Icon: ShieldCheck,
    texto:
      'Uma indisponibilidade é confirmada após falhas consecutivas, reduzindo o risco de registrar oscilações momentâneas como incidentes.',
  },
  {
    Icon: Clock,
    texto:
      'Após a confirmação, o período de indisponibilidade é contabilizado a partir da primeira falha identificada.',
  },
  {
    Icon: AlertTriangle,
    texto:
      'São consideradas falhas as respostas inesperadas, erros do serviço ou ausência de resposta que comprometam o acesso ao recurso monitorado.',
  },
  {
    Icon: Wrench,
    texto:
      'Manutenções programadas, períodos sem monitoramento e ocorrências externas podem receber tratamento específico, conforme as condições de serviço aplicáveis.',
  },
  {
    Icon: Activity,
    texto:
      'Os indicadores representam os componentes efetivamente monitorados e podem não refletir, isoladamente, a experiência de todos os usuários ou funcionalidades da plataforma.',
  },
];

export function HeroBanner({ tenantNome, tenantSlug, status, uptimeMes, uptime90dias, uptime12meses, incidentes30d, latenciaMs }: Props) {
  const color = STATUS_COLOR[status];
  const [showFormula, setShowFormula] = useState(false);

  // Card "Status atual": mostra latência quando UP, label de status caso contrário
  const statusValue = status === 'UP' && latenciaMs != null
    ? `${latenciaMs}ms`
    : STATUS_LABEL[status];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
      {/* Status badge */}
      <div className="mb-6 flex items-center gap-3">
        <span
          className="inline-block h-3 w-3 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-lg font-semibold" style={{ color }}>
          {STATUS_LABEL[status]}
        </span>
      </div>

      <h1 className="mb-1 text-2xl font-bold text-gray-900 dark:text-white">
        {tenantNome}
      </h1>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        {tenantSlug}.woli.com.br
      </p>

      {/* Métricas */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard label={<>Uptime<br />(mês)</>} value={fmtPct(uptimeMes)} />
        <MetricCard label={<>Uptime<br />(90 dias)</>} value={fmtPct(uptime90dias)} />
        <MetricCard label={<>Uptime<br />(12 meses)</>} value={fmtPct(uptime12meses)} />
        <MetricCard label={<>Incidentes<br />(30d)</>} value={String(incidentes30d)} />
        <MetricCard
          label={<>{status === 'UP' && latenciaMs != null ? 'Latência' : 'Status atual'}<br />&nbsp;</>}
          value={statusValue}
          valueColor={color}
        />
      </div>

      {/* "Como calculamos" — explicação da fórmula de disponibilidade para o usuário final */}
      <div className="mt-4">
        <button
          type="button"
          aria-expanded={showFormula}
          aria-controls="formula-disponibilidade"
          onClick={() => setShowFormula((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded text-xs text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <Info size={14} aria-hidden="true" />
          Como calculamos a disponibilidade
        </button>

        {showFormula && (
          <div
            id="formula-disponibilidade"
            role="region"
            aria-label="Como calculamos a disponibilidade"
            className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Como calculamos a disponibilidade
              </p>
              <button
                type="button"
                onClick={() => setShowFormula(false)}
                aria-label="Fechar explicação"
                className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>

            <p className="mb-4 text-xs leading-snug text-gray-600 dark:text-gray-300">
              A disponibilidade apresentada nesta página é calculada com base nas
              verificações automáticas realizadas nos serviços monitorados.
            </p>

            {/* Fórmula — renderizada como expressão matemática (fração) */}
            <div className="mb-4 flex justify-center overflow-x-auto">
              <div className="inline-flex items-center gap-3 rounded-md bg-white px-5 py-4 font-serif text-sm italic text-gray-800 dark:bg-gray-900 dark:text-gray-100">
                <span>Disponibilidade</span>
                <span aria-hidden="true">=</span>
                <span className="inline-flex flex-col items-center text-center leading-tight">
                  <span className="whitespace-nowrap px-2 pb-1">
                    tempo monitorado − indisponibilidade confirmada
                  </span>
                  <span className="h-px w-full bg-gray-400 dark:bg-gray-500" />
                  <span className="px-2 pt-1">tempo monitorado</span>
                </span>
                <span aria-hidden="true">×</span>
                <span>100</span>
              </div>
            </div>

            {/* Frases como itens, com ícone — quebra o bloco de texto */}
            <ul className="space-y-2.5 text-xs leading-snug text-gray-600 dark:text-gray-300">
              {CALCULO_ITENS.map(({ Icon, texto }, i) => (
                <li key={i} className="flex gap-2.5">
                  <Icon
                    size={15}
                    aria-hidden="true"
                    className="mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                  />
                  <span>{texto}</span>
                </li>
              ))}
            </ul>

            {/* Rodapé — observação discreta */}
            <p className="mt-4 border-t border-gray-200 pt-3 text-[11px] leading-snug text-gray-400 dark:border-gray-700 dark:text-gray-500">
              Observação: esta página possui caráter informativo. A apuração de níveis
              de serviço e eventuais compromissos de disponibilidade segue as condições
              estabelecidas no contrato ou SLA aplicável.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, valueColor }: { label: ReactNode; value: string; valueColor?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-center dark:border-gray-700 dark:bg-gray-800">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p
        className="mt-1 text-xl font-bold text-gray-900 dark:text-white"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </p>
    </div>
  );
}
